/* eslint key-spacing:0 no-multi-spaces:0 */
import noUiSlider from 'nouislider';
import Mustache   from 'mustache';

import LicenseInput from './license-input';

class EmissionOffset
{
  constructor() {
    if(document.getElementById('emission-offset-form')) {
      this.calculationVariables = {
        pricePerCO2Ton : 5.58, // €/ton (1000kg === 1 tree)
        unitsPer100Km   : {
          b : 'l',
          d : 'l',
          g : 'kg'
        }
      }
      this.errorHideTimer = null;
      this.init();
    }
  }

  init() {
    // Initialize license
    this.licenseInput = new LicenseInput(document.getElementById('license-inputs'));

    // Gather needed elements to object, so we do not need
    // to search every elements every keyup etc.
    this.elements = {
      errorMessage : document.getElementById('error-message'),
      submitButton : document.getElementById('pay-emission-offset-submit')
    }
    // Initialize events
    this._initSlider();
    this._initSliderEvents();
    this._initInputEvents();
    this._manuallyInsertValuesLinkEvent();
    this._licenseFormEvents();
    this._bodyClickEvent();
    // Precalculate emission offset and emission totals
    this._updatePriceAndEmissionsTotal();
    this._formSubmitEvent();
  }

  /**
   * Initialize mileage slider via noUiSlider
   *
   * @returns {undefined}
   * @private
   */
  _initSlider() {
    noUiSlider.create(document.getElementById('mileage-slider'), {
      range       : {
        min : 0,
        max : 54
      },
      step        : 5,
      start       : 5,
      direction   : 'ltr',
      orientation : 'horizontal',
      connect     : [true, false],
      behaviour   : 'tap-drag',
      tooltips    : true,
      format      : {
        from : (value) => value,
        to   : (value) => `<span class="delta bold">${Math.ceil(value)}</span> <span class="zeta">tkm</span>`
      }
    });
  }

  /**
   * Initializes slider set events
   * Insert slider value to hidden input when slide handle released
   *
   * @returns {undefined}
   * @private
   */
  _initSliderEvents() {
    const self         = this,
      slider       = document.getElementById('mileage-slider'),
      mileageInput = document.getElementById('mileage-per-year');

    slider.noUiSlider.on('slide', (values) => {
      const value = values[0].replace(/\D/g, '');
      if(parseInt(value) < 5) {
        slider.noUiSlider.set(5);
      } else if(parseInt(value) > 50) {
        slider.noUiSlider.set(50);
      } else {
        mileageInput.value = value;
        self._updatePriceAndEmissionsTotal();
      }
    });
  }

  /**
   * Initializes input events. Consumption input and fuel type change events
   *
   * @returns {undefined}
   * @private
   */
  _initInputEvents() {
    const consumptionInput = document.getElementById('consumption'),
      fuelTypeSelect   = document.getElementById('fuel-type');

    consumptionInput.addEventListener('keyup', event => {
      // Check that the input is number
      if(!isNaN(+event.target.value)) {
        this._updatePriceAndEmissionsTotal();
      }
    });

    fuelTypeSelect.addEventListener('change', () => {
      this._updatePriceAndEmissionsTotal();
    });
  }

  /**
   * If user does not know licenseplate, show form bottom
   * Basically just display block to the form bottom
   *
   * @returns {undefined}
   * @private
   */
  _manuallyInsertValuesLinkEvent() {
    const showManualInputsLink = document.getElementById('show-manual-inputs');

    if(showManualInputsLink) {
      showManualInputsLink.addEventListener('click', event => {
        event.preventDefault();
        // Render selectable form
        this._renderCarDetails({ carDetails : false });
        document.getElementById('emission-offset-form-bottom').style.display = 'block';

        const hrLine = document.getElementById('emission-offset-form').querySelector('hr.hidden');
        if(hrLine) {
          hrLine.classList.remove('hidden');
        }
        const tabletLine = document.getElementById('tablet-line-between');
        if(tabletLine.classList.contains('hidden')) {
          tabletLine.classList.remove('hidden');
        }
      });
    }
  }

  /**
   * Create license form events.
   * Includes search button click and focus change when letters are filled
   *
   * @returns {undefined}
   * @private
   */
  _licenseFormEvents() {
    document.getElementById('get-car-emissions').addEventListener('click', () => {
      // Check that license fields are filled
      const license    = this.licenseInput.getLicense(),
        stageToken = document.getElementById('stage-token').value;

      this._getEmissionsByLicense(stageToken, license);
    });

    this.licenseInput.inputs.letterInput.addEventListener('keydown', event => {
      this._hideErrorMessages();
      // If user hits enter, execute search
      if(event.keyCode === 13) {
        event.preventDefault();
        document.getElementById('get-car-emissions').click();
      }
    });

    this.licenseInput.inputs.numberInput.addEventListener('keydown', event => {
      this._hideErrorMessages();
      // If user hits enter, execute search
      if(event.keyCode === 13) {
        event.preventDefault();
        // Emulate button click
        document.getElementById('get-car-emissions').click();
      }
    });

    this.licenseInput.inputs.numberInput.addEventListener('keyup', event => {
      // Enable search button if license input is filled
      document.getElementById('get-car-emissions').disabled =
        event.target.value.length === 0 && this.licenseInput.inputs.letterInput.value.length === 0;
    });

    this.licenseInput.inputs.letterInput.addEventListener('keyup', event => {
      // Enable search button if license input is filled
      document.getElementById('get-car-emissions').disabled =
        event.target.value.length === 0 && this.licenseInput.inputs.numberInput.value.length === 0;
    })
  }

  /**
   * Gather all informations from form, calculates price and updates it to DOM
   *
   * @returns {undefined}
   * @private
   */
  _updatePriceAndEmissionsTotal() {
    // HTML-elements
    const priceElement  = document.getElementById('emission-offset-price'),
      emissionTotal = document.getElementById('emissions-total-amount-per-year');
    // Input values
    const mileagePerYear   = parseInt(document.getElementById('mileage-per-year').value) * 1000,
      selectedFuelType = document.getElementById('fuel-type').querySelector('option:checked').value,
      litersPer100     = document.getElementById('consumption').value;

    let co2Emissions;
    // If we do not know what is the co2 emissions g per 100 km, calculate it
    if(!document.getElementById('known-car-emissions')) {
      co2Emissions = this.constructor.calculateCo2Emissions(selectedFuelType, parseFloat(litersPer100));
    } else {
      co2Emissions = document.getElementById('known-car-emissions').value;
    }
    const emissionsOffsetPrice = this.constructor.calculateEmissionOffsetPrice(co2Emissions, mileagePerYear, this.calculationVariables.pricePerCO2Ton),
      emissionPerYear      = this.constructor.calculateEmissionsPerYear(co2Emissions, mileagePerYear);

    this.elements.submitButton.dataset.gtmValue = emissionsOffsetPrice.toFixed(2);

    priceElement.innerText  = !isNaN(emissionsOffsetPrice) ?
      `${this.constructor.calculateEmissionOffsetPrice(co2Emissions, mileagePerYear, this.calculationVariables.pricePerCO2Ton).toFixed(2).replace('.',',')} €` :
      '--,-- €';

    emissionTotal.innerText = !isNaN(emissionPerYear) ?
      `${this.constructor.calculateEmissionsPerYear(co2Emissions, mileagePerYear).toFixed(1).replace('.',',')} tonnia vuodessa` :
      '--,- tonnia vuodessa';
    document.getElementById('emissions-per-year-rounded').value = Math.round(emissionPerYear);
  }

  /**
   * Get car emission details from trafi via stage-ajax
   *
   * @param {string} stageToken - Stage token, value defaults to null. Mandatory parameter
   * @param {string} license    - Entered licensenumber, value defaults to null. Mandatory parameter.
   *
   * @returns {undefined|boolean} - Returns false, if stageToken or license is null. Otherwise modifies DOM.
   * @private
   */
  _getEmissionsByLicense(stageToken = null, license = null) {
    if(stageToken === null || license === null) {
      return false;
    }
    // Clear error hide timer
    clearTimeout(this.errorHideTimer);
    this.errorHideTimer = null;
    this._toggleDisableInputs(true);

    const httpRequest = new XMLHttpRequest();

    httpRequest.open('GET', '/stage-ajax/get-emissions-by-license/?stageToken=' + stageToken + '&license=' + license);
    // Create event listener for http request
    httpRequest.addEventListener('load', response => {
      if(response.currentTarget.status === 200) {
        const responseParsed = JSON.parse(response.currentTarget.response);

        // If car not found from trafi, show error
        if(!responseParsed.status || responseParsed.status !== 'ok') {
          this._renderCarDetails({ carDetails : false });
          this._initInputEvents();
          this._showErrorMessages();
        } else {
          // Car found from trafi
          this._hideErrorMessages();
          this._renderCarDetails(responseParsed);
          this._updatePriceAndEmissionsTotal();
        }
        // Update stage token
        document.getElementById('stage-token').value = responseParsed.stageToken;
      } else {
        // Bad request or so.
        this._renderCarDetails({ carDetails : false });
        this._initInputEvents();
        this._showErrorMessages();
      }
      const hrLine = document.getElementById('emission-offset-form').querySelector('hr.hidden');
      if(hrLine) {
        hrLine.classList.remove('hidden');
      }

      // Vertical line in tablet sized screens
      const tabletLine = document.getElementById('tablet-line-between');
      if(tabletLine.classList.contains('hidden')) {
        tabletLine.classList.remove('hidden');
      }
      this._toggleDisableInputs(false);
    });
    httpRequest.send();
  }

  /**
   * Show form error messages
   *
   * @returns {undefined}
   * @private
   */
  _showErrorMessages() {
    if(!this.licenseInput.inputs.letterInput.classList.contains('error')) {
      this.licenseInput.inputs.letterInput.classList.add('error');
      this.licenseInput.inputs.numberInput.classList.add('error');
    }

    if(!this.elements.errorMessage.classList.contains('in')) {
      this.elements.errorMessage.classList.add('in');
    }

    // Set timeout for error message if user does not click body for hiding message
    this.errorHideTimer = setTimeout(() => {
      this._hideErrorMessages();
    }, 5000);
  }

  /**
   * Hides form error messages
   *
   * @returns {undefined}
   * @private
   */
  _hideErrorMessages() {
    if(this.licenseInput.inputs.letterInput.classList.contains('error')) {
      this.licenseInput.inputs.letterInput.classList.remove('error');
      this.licenseInput.inputs.numberInput.classList.remove('error');
    }
    if(this.elements.errorMessage.classList.contains('in')) {
      this.elements.errorMessage.classList.remove('in');
    }
  }

  /**
   * Toggle disable statee
   *
   * @param {boolean} disableState - Disable state
   *
   * @returns {undefined}
   * @private
   */
  _toggleDisableInputs(disableState = true) {
    this.licenseInput.inputs.numberInput.disabled = disableState;
    this.licenseInput.inputs.letterInput.disabled = disableState;
    this.elements.errorMessage.disabled           = disableState;
  }

  /**
   * Mustache renders emission offset form
   *
   * @param {object} carDetails - Car details in object. Got from stage-ajax
   *
   * @returns {undefined}
   * @private
   */
  _renderCarDetails(carDetails) {
    const carDetailsTemplate = document.getElementById('car-details-template').innerHTML;
    document.getElementById('car-details-container').innerHTML = Mustache.render(carDetailsTemplate, carDetails);
    document.getElementById('emission-offset-form-bottom').style.display = 'block';
    this._initInputEvents();
    this._updatePriceAndEmissionsTotal();
  }

  _bodyClickEvent() {
    document.addEventListener('click', () => {
      if(this.elements.errorMessage.classList.contains('in')) {
        this._hideErrorMessages();
        clearTimeout(this.errorHideTimer);
      }
    });
  }

  /**
   * Form submit event. Prevent default and send user to ilmastokummit
   *
   * @returns {undefined}
   * @private
   */
  _formSubmitEvent() {
    document.getElementById('emission-offset-form').addEventListener('submit', submitEvent => {
      submitEvent.preventDefault();

      // Add dataLayer event
      const co2Rounded        = document.getElementById('emissions-per-year-rounded').value,
        returnUrl         = document.getElementById('return-url').value,
        partnerPaymentUrl = `https://bo.co2cost.com/act?partner=laakkonen&tco2=${co2Rounded}&retUrl=${returnUrl}`;

      // Send user to ilmastokummit!
      window.location.href = partnerPaymentUrl;
    });
  }

  /**
   * Calculate emission offset with given parameters
   * Used when licenseplate is not giving enough information
   *
   * @param {string} fuelType    - Selected fuel type
   * @param {number} consumption - Consumption
   *
   * @returns {number} - Returns calculated emission offset price
   * @static
   *
   * @see {@link https://ecoscore.be/en/info/ecoscore/co2} for more details about calculation
   */
  static calculateCo2Emissions(fuelType, consumption) {
    let gramsPerUnit;
    switch(fuelType) {
    case 'b': gramsPerUnit = 2392;  break;
    case 'd': gramsPerUnit = 2640;  break;
    case 'g': gramsPerUnit = 2252; break;
    }
    return Math.round(consumption * gramsPerUnit / 100);
  }

  /**
   * Calculates price for emission offset per year
   *
   * @param {number} co2Emissions          - CO2 emissions g/km
   * @param {number} yearlyDrivingMileage  - Yearly driving mileage
   * @param {number} pricePerCO2Ton        - Price per co2 ton
   *
   * @returns {number}
   * @static
   */
  static calculateEmissionOffsetPrice(co2Emissions, yearlyDrivingMileage, pricePerCO2Ton) {
    const co2EmissionsPerYear = (co2Emissions * yearlyDrivingMileage) / 1000000;
    return +(Math.round(Math.round(co2EmissionsPerYear) * pricePerCO2Ton + 'e2') + 'e-2');
  }

  /**
   * Calculate emissions per year
   *
   * @param {number} co2Emissions         - CO2 emissions per kilometer
   * @param {number} yearlyDrivingMileage - Yearly driving kilometers in tkm (50tkm will be converted to 50000)
   *
   * @returns {number} - Emissions in number per year
   * @static
   */
  static calculateEmissionsPerYear(co2Emissions, yearlyDrivingMileage) {
    return co2Emissions * yearlyDrivingMileage / 1000000; // To tons
  }
}
export default EmissionOffset;
